import React, { useState } from 'react';
import { X, FileDown, Calendar, Loader2, CheckCircle2 } from 'lucide-react';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import api from '../api/axios';

// ─── Constantes ───────────────────────────────────────────────────────────────

const MONTH_NAMES = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const PERIODS = [
  { id: 'day',    label: "Aujourd'hui" },
  { id: 'week',   label: 'Semaine'     },
  { id: 'month',  label: 'Mois'        },
  { id: 'year',   label: 'Année'       },
  { id: 'custom', label: 'Personnalisé'},
];

const SECTIONS_CONFIG = [
  { id: 'resume',       label: 'Résumé · KPIs + Ticket moyen'       },
  { id: 'graphe',       label: "Graphe d'évolution du CA"           },
  { id: 'paiements',    label: 'Répartition méthodes de paiement'   },
  { id: 'rdv',          label: 'Statistiques des rendez-vous'        },
  { id: 'staff',        label: 'Performance par coiffeur'            },
  { id: 'transactions', label: 'Transactions détaillées'             },
];

// ─── Palette PDF ──────────────────────────────────────────────────────────────

const P = {
  BLUE:  [37,  99,  235],
  DARK:  [15,  23,  42 ],
  GRAY:  [148, 163, 184],
  WHITE: [255, 255, 255],
  LIGHT: [248, 250, 252],
  GREEN: [21,  128, 61 ],
  RED:   [185, 28,  28 ],
};

// ─── Helpers génériques ───────────────────────────────────────────────────────

const getCurrencySymbol = (d) => ({ XOF: 'FCFA', EUR: '€', USD: '$' })[d] || d || 'FCFA';

/** Séparateur de milliers jsPDF-safe (espace normale, pas \u00A0). */
const fmtNum = (n) =>
  Math.round(n || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

// ─── Helpers période ──────────────────────────────────────────────────────────

const buildPeriodLabel = (period, year, month, start, end) => {
  const fmt = (d) => new Date(d + 'T12:00:00').toLocaleDateString('fr-FR');
  switch (period) {
    case 'day':   return `Aujourd'hui - ${new Date().toLocaleDateString('fr-FR')}`;
    case 'week': {
      const t = new Date(), d = t.getDay(), diff = t.getDate() - d + (d === 0 ? -6 : 1);
      const lun = new Date(t); lun.setDate(diff);
      const dim = new Date(lun); dim.setDate(lun.getDate() + 6);
      return `Semaine du ${lun.toLocaleDateString('fr-FR')} au ${dim.toLocaleDateString('fr-FR')}`;
    }
    case 'month':  return `${MONTH_NAMES[month - 1]} ${year}`;
    case 'year':   return `Année ${year}`;
    case 'custom':
      if (start && end) return `Du ${fmt(start)} au ${fmt(end)}`;
      if (start) return `Depuis le ${fmt(start)}`;
      if (end)   return `Jusqu'au ${fmt(end)}`;
      return 'Période personnalisée';
    default: return '';
  }
};

const buildStatsParams = (period, year, month, start, end) =>
  period === 'custom'
    ? { period: 'custom', ...(start && { startDate: start }), ...(end && { endDate: end }) }
    : { period, year, month };

const buildDateParams = (period, year, month, start, end) => {
  switch (period) {
    case 'day': {
      const t = new Date().toISOString().split('T')[0];
      return { startDate: t, endDate: t };
    }
    case 'week': {
      const t = new Date(), d = t.getDay(), diff = t.getDate() - d + (d === 0 ? -6 : 1);
      const lun = new Date(t); lun.setDate(diff);
      const dim = new Date(lun); dim.setDate(lun.getDate() + 6);
      return { startDate: lun.toISOString().split('T')[0], endDate: dim.toISOString().split('T')[0] };
    }
    case 'month': {
      const first = new Date(year, month - 1, 1);
      const last  = new Date(year, month, 0);
      return { startDate: first.toISOString().split('T')[0], endDate: last.toISOString().split('T')[0] };
    }
    case 'year':
      return { startDate: `${year}-01-01`, endDate: `${year}-12-31` };
    case 'custom':
      return { ...(start && { startDate: start }), ...(end && { endDate: end }) };
    default: return {};
  }
};

// ─── Helpers dates PDF ───────────────────────────────────────────────────────

/** Parse robuste : ISO string ou "DD/MM/YYYY" → timestamp (ms).
 *  Teste DD/MM/YYYY EN PREMIER pour éviter que le navigateur interprète
 *  "06/02/2026" comme Juin 2 (format US MM/DD/YYYY). */
const parseDate = (d) => {
  if (!d) return 0;
  const s = String(d);
  // DD/MM/YYYY détecté en priorité (évite mauvaise interprétation US)
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    return new Date(`${slash[3]}-${slash[2].padStart(2, '0')}-${slash[1].padStart(2, '0')}T12:00:00`).getTime();
  }
  // ISO string ou tout autre format parseable par le navigateur
  const ts = new Date(s).getTime();
  return isNaN(ts) ? 0 : ts;
};

/** Formate une date en DD/MM/YYYY (avec année) quel que soit le format d'entrée. */
const formatTxDate = (d) => {
  const ts = parseDate(d);
  if (!ts) return String(d || '-');
  return new Date(ts).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ─── Blocs PDF ────────────────────────────────────────────────────────────────

/** 4 cartes KPI + ticket moyen. Retourne le nouveau y. */
const drawKPIs = (doc, statsData, transactions, y, pageW, currency) => {
  const totalCA       = statsData.global?.totalCA       ?? 0;
  const totalDepenses = statsData.global?.totalDepenses ?? 0;
  const netResult     = totalCA - totalDepenses;
  const nbVentes      = transactions.filter(tx => ['Vente', 'Prestation'].includes(tx.type)).length;
  const ticketMoyen   = nbVentes > 0 ? Math.round(totalCA / nbVentes) : 0;

  const kpis = [
    { label: "CA Total",      value: `${fmtNum(totalCA)} ${currency}`,       fill: [239,246,255], border: [147,197,253], tc: [30,64,175]  },
    { label: "Dépenses",      value: `${fmtNum(totalDepenses)} ${currency}`, fill: [254,242,242], border: [252,165,165], tc: P.RED        },
    { label: "Résultat net",  value: `${fmtNum(netResult)} ${currency}`,     fill: [240,253,244], border: [134,239,172], tc: P.GREEN      },
    { label: "Ticket moyen",  value: `${fmtNum(ticketMoyen)} ${currency}`,   fill: [255,251,235], border: [253,230,138], tc: [146,64,14]  },
  ];

  const gap  = 3;
  const boxW = (pageW - 28 - gap * 3) / 4;

  kpis.forEach((kpi, i) => {
    const x = 14 + i * (boxW + gap);
    doc.setFillColor(...kpi.fill);
    doc.setDrawColor(...kpi.border);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, boxW, 24, 2.5, 2.5, 'FD');

    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...P.GRAY);
    doc.text(kpi.label.toUpperCase(), x + 3, y + 7);

    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...kpi.tc);
    doc.text(kpi.value, x + 3, y + 18, { maxWidth: boxW - 6 });
  });

  return y + 32;
};

/** Graphe à barres jsPDF. Retourne le nouveau y. */
const drawBarChart = (doc, chartData, y, pageW) => {
  if (!chartData || chartData.length === 0) {
    doc.setFontSize(8); doc.setTextColor(...P.GRAY);
    doc.text('Aucune donnée', pageW / 2, y + 10, { align: 'center' });
    return y + 20;
  }

  const chartX = 14;
  const chartH = 52;
  const chartW = pageW - 28;
  const n      = chartData.length;
  const gap    = n > 15 ? 1 : n > 8 ? 2 : 3;
  const barW   = (chartW - (n - 1) * gap) / n;
  const maxVal = Math.max(...chartData.map(d => d.montant), 1);
  const baseY  = y + chartH;

  chartData.forEach((item, i) => {
    const bx   = chartX + i * (barW + gap);
    const barH = item.montant > 0 ? Math.max((item.montant / maxVal) * chartH, 1.5) : 0;
    const by   = baseY - barH;

    // Barre
    doc.setFillColor(...P.BLUE);
    doc.rect(bx, by, barW, barH, 'F');

    // Valeur au-dessus (seulement si barre assez large)
    if (barW >= 8 && item.montant > 0) {
      doc.setFontSize(5);
      doc.setTextColor(...P.DARK);
      doc.text(fmtNum(item.montant), bx + barW / 2, by - 1.5, { align: 'center' });
    }

    // Label en dessous
    doc.setFontSize(n > 20 ? 5 : 6.5);
    doc.setTextColor(...P.GRAY);
    doc.text(item.label, bx + barW / 2, baseY + 5, { align: 'center' });
  });

  // Ligne de base
  doc.setDrawColor(...P.GRAY);
  doc.setLineWidth(0.25);
  doc.line(chartX, baseY, chartX + chartW, baseY);

  return baseY + 13;
};

/** Tableau répartition paiements. Retourne le nouveau y. */
const drawPaymentBreakdown = (doc, transactions, y, currency) => {
  const ventesTx = transactions.filter(tx => ['Vente', 'Prestation'].includes(tx.type));
  const totalCA  = ventesTx.reduce((s, tx) => s + tx.montant, 0);
  const byMethod = {};
  ventesTx.forEach(tx => {
    const m = tx.methodePaiement || 'Autre';
    if (!byMethod[m]) byMethod[m] = { montant: 0, count: 0 };
    byMethod[m].montant += tx.montant;
    byMethod[m].count++;
  });

  const rows = Object.entries(byMethod)
    .sort((a, b) => b[1].montant - a[1].montant)
    .map(([method, d]) => [
      method,
      d.count.toString(),
      `${fmtNum(d.montant)} ${currency}`,
      totalCA > 0 ? `${Math.round((d.montant / totalCA) * 100)} %` : '0 %',
    ]);

  autoTable(doc, {
    startY: y,
    head:   [['Méthode de paiement', 'Transactions', 'Montant encaissé', 'Part du CA']],
    body:   rows.length > 0 ? rows : [['Aucune vente enregistrée', '-', '-', '-']],
    styles:             { font: 'helvetica', fontSize: 9, cellPadding: 3.5 },
    headStyles:         { fillColor: P.BLUE, textColor: P.WHITE, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: P.LIGHT },
    columnStyles: {
      0: { fontStyle: 'bold' },
      1: { halign: 'center' },
      2: { halign: 'right', fontStyle: 'bold' },
      3: { halign: 'center' },
    },
    margin: { left: 14, right: 14 },
  });

  return doc.lastAutoTable.finalY + 14;
};

/** 4 cartes stats RDV. Retourne le nouveau y. */
const drawBookingStats = (doc, bookings, y, pageW) => {
  const total    = bookings.length;
  const termine  = bookings.filter(b => b.status === 'Terminé').length;
  const annule   = bookings.filter(b => b.status === 'Annulé').length;
  const confirme = bookings.filter(b => b.status === 'Confirmé').length;
  const tauxComp = total > 0 ? Math.round((termine / total) * 100) : 0;
  const tauxAnn  = total > 0 ? Math.round((annule  / total) * 100) : 0;

  const stats = [
    { label: 'Total RDV',  value: total.toString(),               fill: [239,246,255], border: [147,197,253], tc: [30,64,175]  },
    { label: 'Terminés',   value: `${termine}  (${tauxComp} %)`, fill: [240,253,244], border: [134,239,172], tc: P.GREEN      },
    { label: 'Annulés',    value: `${annule}  (${tauxAnn} %)`,   fill: [254,242,242], border: [252,165,165], tc: P.RED        },
    { label: 'En attente', value: confirme.toString(),             fill: [255,251,235], border: [253,230,138], tc: [146,64,14]  },
  ];

  const gap  = 3;
  const boxW = (pageW - 28 - gap * 3) / 4;

  stats.forEach((s, i) => {
    const x = 14 + i * (boxW + gap);
    doc.setFillColor(...s.fill);
    doc.setDrawColor(...s.border);
    doc.setLineWidth(0.3);
    doc.roundedRect(x, y, boxW, 22, 2.5, 2.5, 'FD');

    doc.setFontSize(6);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...P.GRAY);
    doc.text(s.label.toUpperCase(), x + 3, y + 7);

    doc.setFontSize(9.5);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...s.tc);
    doc.text(s.value, x + 3, y + 17, { maxWidth: boxW - 6 });
  });

  return y + 30;
};

/** Camembert (Prestations / Ventes / Dépenses) + légende. Retourne le nouveau y. */
/**
 * Dessine un secteur de camembert en utilisant doc.lines() pour des arcs lisses.
 * Chaque secteur est un polygone fermé : centre → arc → retour centre.
 */
const drawPieSector = (doc, cx, cy, r, startDeg, sweepDeg, color) => {
  if (sweepDeg < 0.01) return;

  // 2 points par degré pour un arc parfaitement lisse
  const steps    = Math.max(4, Math.ceil(sweepDeg * 2));
  const startRad = startDeg * Math.PI / 180;

  // Premier segment : centre → premier point de l'arc
  const lines = [[r * Math.cos(startRad), r * Math.sin(startRad)]];

  let prevX = cx + r * Math.cos(startRad);
  let prevY = cy + r * Math.sin(startRad);

  for (let i = 1; i <= steps; i++) {
    const rad = (startDeg + sweepDeg * (i / steps)) * Math.PI / 180;
    const px  = cx + r * Math.cos(rad);
    const py  = cy + r * Math.sin(rad);
    lines.push([px - prevX, py - prevY]);
    prevX = px;
    prevY = py;
  }

  // Fermeture vers le centre
  lines.push([cx - prevX, cy - prevY]);

  doc.setFillColor(...color);
  doc.setDrawColor(...color);
  doc.setLineWidth(0);
  doc.lines(lines, cx, cy, [1, 1], 'F', false);
};

/** Camembert (Prestations / Ventes / Dépenses) + légende. Retourne le nouveau y. */
const drawTypeBreakdown = (doc, transactions, y, pageW, currency) => {
  const totalPrestation = transactions.filter(tx => tx.type === 'Prestation').reduce((s, tx) => s + tx.montant, 0);
  const totalVente      = transactions.filter(tx => tx.type === 'Vente').reduce((s, tx) => s + tx.montant, 0);
  const totalDepense    = transactions.filter(tx => ['Depense', 'Dépense'].includes(tx.type)).reduce((s, tx) => s + tx.montant, 0);
  const grandTotal      = totalPrestation + totalVente + totalDepense;

  const ALL = [
    { label: 'Prestations',    value: totalPrestation, color: [37,99,235]  },
    { label: 'Ventes produit', value: totalVente,      color: [21,128,61]  },
    { label: 'Dépenses',       value: totalDepense,    color: [185,28,28]  },
  ];

  const r  = 28;           // rayon (mm) - assez grand pour que 1 % reste visible
  const cx = 14 + r + 2;  // centre x
  const cy = y + r + 4;   // centre y

  if (grandTotal === 0) {
    doc.setFontSize(8); doc.setTextColor(...P.GRAY);
    doc.text('Aucune transaction sur la période.', 14, y + 10);
    return y + 18;
  }

  // ── Calcul des angles avec angle minimum garanti ──────────────────────────
  // Chaque part non nulle obtient au moins MIN_DEG°, le reste est proportionnel.
  const MIN_DEG   = 8;
  const nonZero   = ALL.filter(s => s.value > 0);
  const reserved  = nonZero.length * MIN_DEG;
  const remaining = Math.max(0, 360 - reserved);

  const withAngles = ALL.map(item => {
    if (item.value === 0) return { ...item, sweep: 0 };
    const proportional = (item.value / grandTotal) * remaining;
    return { ...item, sweep: MIN_DEG + proportional };
  });

  // ── Dessin des secteurs ───────────────────────────────────────────────────
  if (nonZero.length === 1) {
    doc.setFillColor(...nonZero[0].color);
    doc.circle(cx, cy, r, 'F');
  } else {
    let startAngle = -90;
    withAngles.forEach(item => {
      drawPieSector(doc, cx, cy, r, startAngle, item.sweep, item.color);
      startAngle += item.sweep;
    });

    // Lignes séparatrices blanches fines entre les parts
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.6);
    let sa = -90;
    withAngles.forEach(item => {
      if (item.sweep === 0) return;
      const rad = sa * Math.PI / 180;
      doc.line(cx, cy, cx + r * Math.cos(rad), cy + r * Math.sin(rad));
      sa += item.sweep;
    });
  }

  // ── Légende à droite ──────────────────────────────────────────────────────
  const legendX = cx + r + 10;
  let   ly      = y + 6;

  ALL.forEach(item => {
    const pct = grandTotal > 0 ? ((item.value / grandTotal) * 100).toFixed(1) : '0.0';

    doc.setFillColor(...item.color);
    doc.roundedRect(legendX, ly, 5, 5, 0.8, 0.8, 'F');

    doc.setFontSize(8);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...P.DARK);
    doc.text(item.label, legendX + 7, ly + 4);

    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...P.GRAY);
    doc.text(`${fmtNum(item.value)} ${currency}  -  ${pct} %`, legendX + 7, ly + 10);

    ly += 18;
  });

  return Math.max(cy + r + 8, ly + 2);
};

// ─── Générateur PDF principal ─────────────────────────────────────────────────

const generatePDF = ({ statsData, transactions, bookings }, salonNom, devise, periodLabel, sections, orientation) => {
  const isLandscape = orientation === 'landscape';
  const doc      = new jsPDF(isLandscape ? 'l' : 'p', 'mm', 'a4');
  const pageW    = doc.internal.pageSize.getWidth();
  const pageH    = doc.internal.pageSize.getHeight();
  const currency = getCurrencySymbol(devise);

  // ── En-tête ──────────────────────────────────────────────────────────────
  doc.setFillColor(...P.BLUE);
  doc.rect(0, 0, pageW, 44, 'F');

  doc.setTextColor(...P.WHITE);
  doc.setFontSize(20);
  doc.setFont('helvetica', 'bold');
  doc.text(salonNom || 'SamaSalon', 14, 16);

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Rapport Financier', 14, 24);

  doc.setFontSize(8.5);
  doc.text(periodLabel, 14, 32);

  doc.setFontSize(7.5);
  doc.text(
    `Généré le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`,
    pageW - 14, 38, { align: 'right' }
  );

  let y = 56;

  // Utilitaire : vérifie si on a assez de place, sinon nouvelle page
  const checkPageBreak = (needed) => {
    if (y + needed > pageH - 22) { doc.addPage(); y = 20; }
  };

  // Utilitaire : titre de section
  const sectionTitle = (title) => {
    checkPageBreak(18);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...P.DARK);
    doc.text(title, 14, y);
    y += 5;
  };

  // ── Résumé ───────────────────────────────────────────────────────────────
  if (sections.resume) {
    sectionTitle('Résumé de la période');
    checkPageBreak(32);
    y = drawKPIs(doc, statsData, transactions, y, pageW, currency);
  }

  // ── Graphe ───────────────────────────────────────────────────────────────
  if (sections.graphe && (statsData.chartData || []).length > 0) {
    checkPageBreak(70);
    sectionTitle("Évolution du chiffre d'affaires");
    y = drawBarChart(doc, statsData.chartData, y, pageW);
  }

  // ── Répartition paiements ─────────────────────────────────────────────────
  if (sections.paiements) {
    checkPageBreak(30);
    sectionTitle('Répartition des paiements');
    y = drawPaymentBreakdown(doc, transactions, y, currency);
  }

  // ── Stats RDV ─────────────────────────────────────────────────────────────
  if (sections.rdv) {
    checkPageBreak(40);
    sectionTitle('Statistiques des rendez-vous');
    y = drawBookingStats(doc, bookings, y, pageW);
  }

  // ── Performance staff ─────────────────────────────────────────────────────
  if (sections.staff) {
    checkPageBreak(30);
    sectionTitle('Performance par coiffeur');
    const empRows = (statsData.byEmployee || []).map((emp, idx) => [
      `${idx + 1}`,
      `${emp.prenom} ${emp.nom}`,
      emp.nbServices.toString(),
      `${fmtNum(emp.ca)} ${currency}`,
    ]);
    autoTable(doc, {
      startY: y,
      head:   [['#', 'Coiffeur', 'Services', 'CA']],
      body:   empRows.length > 0 ? empRows : [['-', 'Aucune donnée', '-', '-']],
      styles:             { font: 'helvetica', fontSize: 9, cellPadding: 4 },
      headStyles:         { fillColor: P.BLUE, textColor: P.WHITE, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: P.LIGHT },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: isLandscape ? 130 : 85 },
        2: { cellWidth: 28, halign: 'center' },
        3: { halign: 'right', fontStyle: 'bold' },
      },
      margin: { left: 14, right: 14 },
    });
    y = doc.lastAutoTable.finalY + 14;
  }

  // ── Transactions ──────────────────────────────────────────────────────────
  if (sections.transactions) {
    checkPageBreak(30);
    sectionTitle(`Transactions de la période (${transactions.length})`);

    // Totaux par type + graphe comparatif avant le tableau
    checkPageBreak(80);
    y = drawTypeBreakdown(doc, transactions, y, pageW, currency);
    checkPageBreak(10);

    const txRows = [...transactions]
      .sort((a, b) => parseDate(a.date) - parseDate(b.date))
      .map(tx => [
        formatTxDate(tx.date),
        tx.description || '-',
        tx.type,
        `${fmtNum(tx.montant)} ${currency}`,
        tx.methodePaiement || '-',
      ]);
    autoTable(doc, {
      startY: y,
      head:   [['Date', 'Description', 'Type', 'Montant', 'Paiement']],
      body:   txRows.length > 0 ? txRows : [['-', 'Aucune transaction sur la période', '-', '-', '-']],
      styles:             { font: 'helvetica', fontSize: 8, cellPadding: 3 },
      headStyles:         { fillColor: [51, 65, 85], textColor: P.WHITE, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: P.LIGHT },
      columnStyles: {
        0: { cellWidth: 22 },
        1: { cellWidth: 'auto' },
        2: { cellWidth: 22 },
        3: { cellWidth: isLandscape ? 58 : 44, halign: 'right', fontStyle: 'bold' },
        4: { cellWidth: 32 },
      },
      margin: { left: 14, right: 14 },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index === 2) {
          data.cell.styles.textColor = ['Vente', 'Prestation'].includes(data.cell.raw) ? [5, 150, 105] : [220, 38, 38];
          data.cell.styles.fontStyle = 'bold';
        }
      },
    });
  }

  // ── Pied de page sur toutes les pages ────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setDrawColor(...P.GRAY);
    doc.setLineWidth(0.2);
    doc.line(14, pageH - 14, pageW - 14, pageH - 14);
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...P.GRAY);
    doc.text('SamaSalon - Document confidentiel', 14, pageH - 8);
    doc.text(`Page ${i} / ${totalPages}`, pageW - 14, pageH - 8, { align: 'right' });
  }

  const filename = `rapport-${(salonNom || 'salon').toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(filename);
};

// ─── Composant modal ──────────────────────────────────────────────────────────

const ExportReportModal = ({ onClose, salonNom, salonDevise, salonId }) => {
  const now = new Date();

  const [period,        setPeriod]        = useState('month');
  const [selectedYear,  setSelectedYear]  = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const [startDate,     setStartDate]     = useState('');
  const [endDate,       setEndDate]       = useState('');
  const [orientation,   setOrientation]   = useState('portrait');
  const [sections,      setSections]      = useState({
    resume: true, graphe: true, paiements: true, rdv: true, staff: true, transactions: true,
  });
  const [loading, setLoading] = useState(false);
  const [done,    setDone]    = useState(false);
  const [error,   setError]   = useState('');

  const periodLabel    = buildPeriodLabel(period, selectedYear, selectedMonth, startDate, endDate);
  const selectedCount  = Object.values(sections).filter(Boolean).length;
  const toggleSection  = (id) => setSections(s => ({ ...s, [id]: !s[id] }));

  const handleGenerate = async () => {
    if (period === 'custom' && !startDate && !endDate) {
      setError('Veuillez renseigner au moins une date.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const statsParams = buildStatsParams(period, selectedYear, selectedMonth, startDate, endDate);
      const dateParams  = buildDateParams(period, selectedYear, selectedMonth, startDate, endDate);

      const [statsRes, txRes, bookingsRes] = await Promise.all([
        api.get('/admin/stats/finances', { params: statsParams }),
        api.get('/admin/transactions',   { params: { ...dateParams, limit: 500 } }),
        sections.rdv && salonId
          ? api.get(`/admin/bookings/${salonId}`, { params: dateParams })
          : Promise.resolve({ data: { data: [] } }),
      ]);

      generatePDF(
        {
          statsData:    statsRes.data.data,
          transactions: txRes.data.data     || [],
          bookings:     bookingsRes.data.data || [],
        },
        salonNom, salonDevise, periodLabel, sections, orientation
      );

      setDone(true);
      setTimeout(() => setDone(false), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors de la génération du rapport.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-md flex items-end sm:items-center justify-center z-[110] p-0 sm:p-4">
      <div className="bg-white rounded-t-[2.5rem] sm:rounded-[2.5rem] w-full max-w-md shadow-2xl relative max-h-[92vh] flex flex-col">

        {/* ── En-tête ── */}
        <div className="p-6 pb-4 border-b border-slate-100 shrink-0">
          <button onClick={onClose}
            className="absolute top-5 right-5 text-slate-300 hover:text-slate-600 transition-colors p-1">
            <X size={22} />
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center">
              <FileDown size={20} />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-900">Exporter un rapport</h2>
              <p className="text-slate-400 text-xs font-medium">PDF · {salonNom}</p>
            </div>
          </div>
        </div>

        {/* ── Corps scrollable ── */}
        <div className="flex-1 overflow-y-auto p-6 space-y-5">

          {/* Période */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Période</label>
            <div className="grid grid-cols-3 gap-2">
              {PERIODS.map(p => (
                <button key={p.id}
                  onClick={() => { setPeriod(p.id); setError(''); }}
                  className={`py-2.5 px-3 rounded-xl font-black text-xs transition-all border ${
                    period === p.id
                      ? 'bg-blue-600 text-white border-blue-600 shadow-md'
                      : 'bg-slate-50 text-slate-500 border-slate-100 hover:border-blue-200'
                  }`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sélecteurs contextuels */}
          {period === 'month' && (
            <div className="flex items-center gap-3">
              <select value={selectedMonth}
                onChange={e => setSelectedMonth(Number(e.target.value))}
                className="flex-1 bg-slate-50 border border-slate-200 rounded-xl px-3 py-2.5 text-sm font-bold outline-none cursor-pointer focus:ring-2 ring-blue-500/20">
                {MONTH_NAMES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
              </select>
              <YearStepper year={selectedYear} setYear={setSelectedYear} />
            </div>
          )}

          {period === 'year' && (
            <div className="flex justify-center">
              <YearStepper year={selectedYear} setYear={setSelectedYear} />
            </div>
          )}

          {period === 'custom' && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <Calendar size={10} className="inline mr-1" />Début
                </label>
                <input type="date"
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-bold text-slate-700 text-sm focus:ring-2 ring-blue-500/20"
                  value={startDate}
                  onChange={e => { setStartDate(e.target.value); setError(''); }} />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  <Calendar size={10} className="inline mr-1" />Fin
                </label>
                <input type="date"
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl outline-none font-bold text-slate-700 text-sm focus:ring-2 ring-blue-500/20"
                  value={endDate} min={startDate}
                  onChange={e => { setEndDate(e.target.value); setError(''); }} />
              </div>
            </div>
          )}

          {/* Bandeau période */}
          {periodLabel && (
            <div className="flex items-center gap-2 px-4 py-2.5 bg-blue-50 rounded-2xl border border-blue-100">
              <Calendar size={14} className="text-blue-500 shrink-0" />
              <span className="text-blue-700 font-bold text-xs">{periodLabel}</span>
            </div>
          )}

          {/* Orientation */}
          <div className="space-y-2">
            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
              Orientation de la page
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { id: 'portrait',   label: 'Portrait',  shape: 'h-8 w-6' },
                { id: 'landscape',  label: 'Paysage',   shape: 'h-6 w-8' },
              ].map(o => (
                <button key={o.id}
                  onClick={() => setOrientation(o.id)}
                  className={`flex items-center justify-center gap-2.5 py-3 rounded-xl font-black text-xs transition-all border ${
                    orientation === o.id
                      ? 'bg-slate-900 text-white border-slate-900 shadow-md'
                      : 'bg-slate-50 text-slate-500 border-slate-100 hover:border-slate-300'
                  }`}>
                  <div className={`${o.shape} border-2 rounded-sm ${orientation === o.id ? 'border-slate-400' : 'border-slate-300'}`} />
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Sections à inclure */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Sections à inclure
              </label>
              <span className="text-[10px] font-black text-blue-500 bg-blue-50 px-2 py-0.5 rounded-lg">
                {selectedCount} / {SECTIONS_CONFIG.length}
              </span>
            </div>
            <div className="space-y-1.5">
              {SECTIONS_CONFIG.map(s => (
                <button key={s.id} type="button"
                  onClick={() => toggleSection(s.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-2xl border-2 transition-all text-left ${
                    sections[s.id]
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-slate-50 border-slate-100 opacity-55'
                  }`}>
                  <div className={`w-5 h-5 rounded-lg flex items-center justify-center shrink-0 transition-all ${
                    sections[s.id] ? 'bg-blue-600' : 'bg-slate-200'
                  }`}>
                    {sections[s.id] && <CheckCircle2 size={12} className="text-white" />}
                  </div>
                  <span className="text-xs font-bold text-slate-700 leading-tight">{s.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Erreur */}
          {error && (
            <div className="p-3 bg-red-50 border-l-4 border-red-500 text-red-700 text-xs font-bold rounded-r-xl">
              {error}
            </div>
          )}
        </div>

        {/* ── Pied fixe ── */}
        <div className="px-6 pb-6 pt-3 border-t border-slate-100 shrink-0">
          <button
            onClick={handleGenerate}
            disabled={loading || done || selectedCount === 0}
            className={`w-full flex items-center justify-center gap-2.5 py-4 rounded-2xl font-black text-sm shadow-lg transition-all active:scale-95 ${
              done
                ? 'bg-emerald-500 text-white shadow-emerald-200'
                : 'bg-blue-600 text-white hover:bg-blue-700 shadow-blue-200 disabled:opacity-50 disabled:cursor-not-allowed'
            }`}>
            {loading ? (
              <><Loader2 size={18} className="animate-spin" /> Génération en cours…</>
            ) : done ? (
              <><CheckCircle2 size={18} /> PDF téléchargé !</>
            ) : (
              <><FileDown size={18} /> Générer en {orientation === 'landscape' ? 'Paysage' : 'Portrait'}</>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

// ─── Sous-composant ───────────────────────────────────────────────────────────

const YearStepper = ({ year, setYear }) => (
  <div className="flex items-center bg-slate-50 border border-slate-200 rounded-xl overflow-hidden shrink-0">
    <button onClick={() => setYear(y => y - 1)}
      className="px-3 py-2.5 hover:bg-slate-200 transition-colors text-slate-600 font-bold text-lg leading-none">
      ‹
    </button>
    <span className="px-4 text-sm font-black min-w-[60px] text-center text-slate-800">{year}</span>
    <button onClick={() => setYear(y => y + 1)}
      className="px-3 py-2.5 hover:bg-slate-200 transition-colors text-slate-600 font-bold text-lg leading-none">
      ›
    </button>
  </div>
);

export default ExportReportModal;
